import React, { useState, useRef, useMemo } from 'react';
import { X, Send, Loader2, BookOpen, ExternalLink } from 'lucide-react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

interface Source {
    url: string;
    title?: string | null;
}

interface KnowledgeBasePanelProps {
    onClose: () => void;
    tenant?: string;
}

const KB_URL_DEFAULT = 'https://2wm-ai-bot.fly.dev/chat';

marked.use({ breaks: true });

const KnowledgeBasePanel: React.FC<KnowledgeBasePanelProps> = ({ onClose, tenant = 'phq' }) => {
    const [question, setQuestion] = useState('');
    const [answer, setAnswer] = useState<string | null>(null);
    const [sources, setSources] = useState<Source[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const answerHtml = useMemo(() => {
        if (!answer) return null;
        return DOMPurify.sanitize(marked.parse(answer) as string);
    }, [answer]);

    const handleAsk = async () => {
        const trimmed = question.trim();
        if (!trimmed || isLoading) return;

        setIsLoading(true);
        setAnswer(null);
        setSources([]);
        setError(null);

        try {
            const kbUrl = window.__ENV__?.URL_AI_KNOWLEDGE_BASE || KB_URL_DEFAULT;
            const response = await fetch(kbUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Tenant-ID': tenant,
                },
                body: JSON.stringify({
                    messages: [{ role: 'user', content: trimmed }],
                }),
            });

            if (!response.ok) {
                throw new Error(`Request failed with status ${response.status}`);
            }

            const data = await response.json();
            setAnswer(data.response);
            setSources(data.sources ?? []);
        } catch (err) {
            setError('Failed to get an answer. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleAsk();
        }
    };

    return (
        <div className="w-80 h-full bg-surface border-l border-line flex flex-col transition-all duration-300 ease-in-out">
            {/* Header */}
            <div className="pl-5 py-4 pr-3 border-b border-line flex items-center justify-between h-[72px]">
                <div className="flex items-center gap-2">
                    <BookOpen size={18} className="text-ink-muted-strong" />
                    <h3 className="text-base font-semibold text-ink">Knowledge Base</h3>
                </div>
                <button
                    onClick={onClose}
                    className="text-ink-muted hover:text-ink-secondary transition-colors"
                >
                    <X className="button p-1.5" size={28} />
                </button>
            </div>

            {/* Content */}
            <div className="flex flex-col flex-1 p-3 gap-3 overflow-y-auto">
                {/* Question input */}
                <div className="relative">
                    <textarea
                        ref={textareaRef}
                        value={question}
                        onChange={(e) => setQuestion(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Ask a question..."
                        rows={4}
                        className="w-full resize-none rounded-lg bg-surface-muted px-3 py-2 pr-10 text-sm text-ink placeholder:text-ink-muted outline-none focus:ring-2 focus:ring-blue-500 focus:bg-surface border-0"
                    />
                    <button
                        onClick={handleAsk}
                        disabled={!question.trim() || isLoading}
                        className="absolute bottom-2 right-2 text-blue-600 disabled:text-zinc-300 hover:text-blue-700 transition-colors"
                    >
                        {isLoading ? (
                            <Loader2 size={18} className="animate-spin" />
                        ) : (
                            <Send size={18} />
                        )}
                    </button>
                </div>

                <p className="text-xs text-ink-muted">Press Enter to ask, Shift+Enter for new line</p>

                {/* Loading */}
                {isLoading && (
                    <div className="flex items-center gap-2 text-sm text-ink-muted mt-2">
                        <Loader2 size={14} className="animate-spin" />
                        Searching knowledge base...
                    </div>
                )}

                {/* Error */}
                {error && (
                    <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-600">
                        {error}
                    </div>
                )}

                {/* Answer */}
                {answerHtml && (
                    <div
                        className="rounded-lg bg-canvas border border-line p-3 text-sm text-ink leading-relaxed prose prose-sm max-w-none prose-zinc"
                        dangerouslySetInnerHTML={{ __html: answerHtml }}
                    />
                )}

                {/* Sources */}
                {sources.length > 0 && (
                    <div className="flex flex-col gap-1.5">
                        <p className="text-xs font-medium text-ink-muted uppercase tracking-wide">Sources</p>
                        {sources.map((source, i) => (
                            <a
                                key={i}
                                href={source.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 hover:underline truncate"
                            >
                                <ExternalLink size={11} className="shrink-0" />
                                <span className="truncate">{source.title || source.url}</span>
                            </a>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default KnowledgeBasePanel;
